import React, { useEffect, useState } from 'react';
import { Table, Select, Input, Button, Space, message, Spin } from 'antd';
import axiosInstance from '../../../axiosInstance';

const { Option } = Select;

const UserPermissionTable = () => {
    const [userPermissions, setUserPermissions] = useState([]); // User permissions displayed in the table
    const [allUsers, setAllUsers] = useState([]); // List of all available users for dropdown
    const [loading, setLoading] = useState(true); // Loader state

    useEffect(() => {
        const fetchUserPermissions = async () => {
            try {
                const response = await axiosInstance.get('/user-permission');
                setUserPermissions(response.data || []);
            } catch (error) {
                message.error('Error fetching user permissions.');
                console.error(error);
            } finally {
                setLoading(false); // Set loading to false after data is fetched
            }
        };

        const fetchAllUsers = async () => {
            try {
                const response = await axiosInstance.get('/employees');
                const users = response.data.employees.map(emp => emp.user); // Extract user object
                setAllUsers(users);
            } catch (error) {
                message.error('Error fetching all users.');
                console.error(error);
            }
        };
        
        fetchUserPermissions();
        fetchAllUsers();
    }, []);

    const saveUserPermissionChange = async (updatedPermission) => {
        const permissionData = {
            user_id: updatedPermission.user_id,
            is_approve: updatedPermission.is_approve,
            is_positive_adjustment: updatedPermission.is_positive_adjustment,
            is_negative_adjustment: updatedPermission.is_negative_adjustment,
            is_wastage: updatedPermission.is_wastage,
            is_reverse: updatedPermission.is_reverse,
            is_transfer: updatedPermission.is_transfer,
            is_register_product: updatedPermission.is_register_product,
            is_view_profit : updatedPermission.is_view_profit
        };

        try {
            let response;
            if (updatedPermission.id) {
                // If user permission already exists, update using the id
                response = await axiosInstance.put(`/user-permission/${updatedPermission.id}`, permissionData);
            } else {
                // If new user permission, create
                response = await axiosInstance.post('/user-permission', permissionData);
            }
            const updatedData = response.data;
            setUserPermissions((prevPermissions) =>
                prevPermissions.map((permission) =>
                    permission.id === updatedPermission.id
                        ? { ...permission, id: updatedData.id } // Update ID if it's a new user permission
                        : permission
                )
            );
            message.success(updatedPermission.id ? 'User permission updated successfully.' : 'User permission created successfully.');
        } catch (error) {
            message.error('Error saving user permission.');
            console.error(error);
        }
    };

    const handleUserPermissionChange = (index, field, value) => {
        const updatedPermission = {
            ...userPermissions[index],
            [field]: value,
        };

        setUserPermissions((prevPermissions) => {
            const updatedPermissions = [...prevPermissions];
            updatedPermissions[index] = updatedPermission;
            return updatedPermissions;
        });

        saveUserPermissionChange(updatedPermission); // Save changes automatically
    };

    const addPermissionRow = () => {
        setUserPermissions((prevPermissions) => [
            ...prevPermissions,
            { id: null, user_id: '', is_approve: false, is_positive_adjustment: false, is_negative_adjustment: false, is_reverse: false, is_view_profit: false },
        ]);
    };

    const removePermissionRow = async (index) => {
        const permissionToRemove = userPermissions[index];

        if (permissionToRemove.id) {
            try {
                await axiosInstance.delete(`/user-permission/${permissionToRemove.id}`);
                message.success('User permission removed successfully.');
            } catch (error) {
                message.error('Error removing user permission.');
                console.error(error);
            }
        }

        setUserPermissions((prevPermissions) => prevPermissions.filter((_, i) => i !== index));
    };

    const columns = [
        {
            title: 'User',
            dataIndex: 'user_id',
            render: (userId, record, index) => {
                const selectedUser = allUsers.find((user) => user.id === userId);
                // console.log(allUsers);
                return (
                    <Select
                        showSearch
                        value={selectedUser ? selectedUser.name : ''}
                        onChange={(value) => handleUserPermissionChange(index, 'user_id', value)}
                        style={{ width: '100%' }}
                        placeholder="Select User"
                        optionFilterProp="children"
                        filterOption={(input, option) => {
                            const optionChildren = option?.children?.toLowerCase() || ''; // Ensuring option.children exists
                            return optionChildren.includes(input.toLowerCase());
                        }}
                        
                    >
                        {allUsers
                        .filter((user) => !userPermissions.some((r) => r.user_id === user.id))
                        .map((user) => (
                            <Option key={user.id} value={user.id}>
                                {user.name}
                            </Option>
                        ))}
                          
                    </Select>
                    
                );
            },
        },
        {
            title: 'Is Approved',
            dataIndex: 'is_approve',
            render: (_, record, index) => {
                return (
                    <Input
                        type="checkbox"
                        checked={record.is_approve}
                        onChange={(e) => handleUserPermissionChange(index, 'is_approve', e.target.checked)}
                    />
                );
            },
        },
        {
            title: 'Positive Adjustment',
            dataIndex: 'is_positive_adjustment',
            render: (_, record, index) => {
                return (
                    <Input
                        type="checkbox"
                        checked={record.is_positive_adjustment}
                        onChange={(e) => handleUserPermissionChange(index, 'is_positive_adjustment', e.target.checked)}
                    />
                );
            },
        },
        {
            title: 'Negative Adjustment',
            dataIndex: 'is_negative_adjustment',
            render: (_, record, index) => {
                return (
                    <Input
                        type="checkbox"
                        checked={record.is_negative_adjustment}
                        onChange={(e) => handleUserPermissionChange(index, 'is_negative_adjustment', e.target.checked)}
                    />
                );
            },
        },
        {
            title: 'Wastage Adjustment',
            dataIndex: 'is_wastage',
            render: (_, record, index) => {
                return (
                    <Input
                        type="checkbox"
                        checked={record.is_wastage}
                        onChange={(e) => handleUserPermissionChange(index, 'is_wastage', e.target.checked)}
                    />
                );
            },
        },
        {
            title: 'Reverse Paid Transaction',
            dataIndex: 'is_reverse',
            render: (_, record, index) => {
                return (
                    <Input
                        type="checkbox"
                        checked={record.is_reverse}
                        onChange={(e) => handleUserPermissionChange(index, 'is_reverse', e.target.checked)}
                    />
                );
            },
        },
        {
            title: 'Is transfer',
            dataIndex: 'is_transfer',
            render: (_, record, index) => {
                return (
                    <Input
                        type="checkbox"
                        checked={record.is_transfer}
                        onChange={(e) => handleUserPermissionChange(index, 'is_transfer', e.target.checked)}
                    />
                );
            },
        },
        {
            title: 'Is Register Product',
            dataIndex: 'is_register_product',
            render: (_, record, index) => {
                return (
                    <Input
                        type="checkbox"
                        checked={record.is_register_product}
                        onChange={(e) => handleUserPermissionChange(index, 'is_register_product', e.target.checked)}
                    />
                );
            },
        },
        {
            title: 'Is View Profit',
            dataIndex: 'is_view_profit',
            render: (_, record, index) => {
                return (
                    <Input
                        type="checkbox"
                        checked={record.is_view_profit}
                        onChange={(e) => handleUserPermissionChange(index, 'is_view_profit', e.target.checked)}
                    />
                );
            },
        },
        {
            title: 'Action',
            render: (_, __, index) => {
                return (
                    <Button
                        type="link"
                        danger
                        onClick={() => removePermissionRow(index)}
                    >
                        Remove
                    </Button>
                );
            },
        },
    ];

    return (
        <div>
            <h2>User Permission Table</h2>
            <Table
                dataSource={userPermissions}
                columns={columns}
                pagination={false}
                rowKey={(record) => record.id || `new-${record.user_id}`}
                style={{ marginBottom: '20px' }}
                scroll={{ x: true }}
                loading={loading}
            />
            <Space style={{ marginBottom: '20px', display: 'block', textAlign: 'center' }}>
                <Button type="dashed" onClick={addPermissionRow}>
                    Add Permission
                </Button>
            </Space>
        </div>
    );
};

export default UserPermissionTable;
